export type WorkflowUiLocale = 'en' | 'es' | 'ca' | 'fr' | 'pt' | 'it' | 'de';

export type WorkflowUiCopy = {
  back: string;
  next: string;
  allSteps: string;
  startOver: string;
  stepByStep: string;
  stepsNav: string;
  stepLabel: (number: number, title: string) => string;
};

const COPY: Record<WorkflowUiLocale, WorkflowUiCopy> = {
  en: {
    back: 'Back',
    next: 'Next',
    allSteps: 'All steps',
    startOver: 'Start over',
    stepByStep: 'Step by step',
    stepsNav: 'Workflow steps',
    stepLabel: (number, title) => `Step ${number}: ${title}`,
  },
  es: {
    back: 'Atrás',
    next: 'Siguiente',
    allSteps: 'Todos los pasos',
    startOver: 'Empezar de nuevo',
    stepByStep: 'Paso a paso',
    stepsNav: 'Pasos del flujo',
    stepLabel: (number, title) => `Paso ${number}: ${title}`,
  },
  ca: {
    back: 'Enrere',
    next: 'Següent',
    allSteps: 'Tots els passos',
    startOver: 'Tornar a començar',
    stepByStep: 'Pas a pas',
    stepsNav: 'Passos del flux',
    stepLabel: (number, title) => `Pas ${number}: ${title}`,
  },
  fr: {
    back: 'Retour',
    next: 'Suivant',
    allSteps: 'Toutes les étapes',
    startOver: 'Recommencer',
    stepByStep: 'Étape par étape',
    stepsNav: 'Étapes du flux',
    stepLabel: (number, title) => `Étape ${number} : ${title}`,
  },
  pt: {
    back: 'Voltar',
    next: 'Seguinte',
    allSteps: 'Todos os passos',
    startOver: 'Recomeçar',
    stepByStep: 'Passo a passo',
    stepsNav: 'Passos do fluxo',
    stepLabel: (number, title) => `Passo ${number}: ${title}`,
  },
  it: {
    back: 'Indietro',
    next: 'Avanti',
    allSteps: 'Tutti i passaggi',
    startOver: 'Ricomincia',
    stepByStep: 'Passo dopo passo',
    stepsNav: 'Passaggi del flusso',
    stepLabel: (number, title) => `Passaggio ${number}: ${title}`,
  },
  de: {
    back: 'Zurück',
    next: 'Weiter',
    allSteps: 'Alle Schritte',
    startOver: 'Von vorn beginnen',
    stepByStep: 'Schritt für Schritt',
    stepsNav: 'Ablaufschritte',
    stepLabel: (number, title) => `Schritt ${number}: ${title}`,
  },
};

const MARKERS: Record<WorkflowUiLocale, string[]> = {
  es: [
    ' que ',
    ' los ',
    ' las ',
    ' para ',
    ' una ',
    ' del ',
    ' por ',
    ' como ',
    ' este ',
    ' esta ',
    ' desde ',
    ' tus ',
  ],
  en: [' the ', ' and ', ' with ', ' your ', ' this ', ' from ', ' that ', ' when ', ' using ', ' into '],
  ca: [' els ', ' les ', ' amb ', ' dels ', ' aquest ', ' aquesta ', ' pels '],
  fr: [' les ', ' des ', ' une ', ' pour ', ' dans ', ' est ', ' avec ', ' vos '],
  pt: [' uma ', ' dos ', ' das ', ' você ', ' seus ', ' suas ', ' pelo '],
  it: [' dei ', ' delle ', ' della ', ' una ', ' con ', ' questo ', ' questa '],
  de: [' und ', ' der ', ' die ', ' das ', ' mit ', ' für ', ' nicht ', ' den '],
};

export function detectWorkflowLocale(text: string): WorkflowUiLocale {
  const normalized = ` ${text.toLowerCase().replace(/\s+/g, ' ')} `;
  let best: WorkflowUiLocale = 'en';
  let bestScore = 0;

  (Object.keys(MARKERS) as WorkflowUiLocale[]).forEach(locale => {
    const score = MARKERS[locale].reduce((total, marker) => {
      let from = 0;
      let count = 0;
      while (from < normalized.length) {
        const at = normalized.indexOf(marker, from);
        if (at === -1) break;
        count += 1;
        from = at + marker.length;
      }
      return total + count;
    }, 0);

    if (score > bestScore) {
      best = locale;
      bestScore = score;
    }
  });

  return best;
}

export function getWorkflowUiCopy(text: string): WorkflowUiCopy {
  return COPY[detectWorkflowLocale(text)];
}
